import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Star,
  MapPin,
  CheckCircle2,
  ShieldCheck,
  Heart,
  Calendar,
  Clock,
  Sparkles,
  User,
  Award,
  Languages,
  GraduationCap,
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Maximize2,
  Image as ImageIcon,
  Compass,
  Car,
  AlertTriangle
} from 'lucide-react';
import { Companion, BookingDetails, BookingTier } from '../types';
import {
  OCCASIONS_LIST,
  isDemoCompanionProfile,
  getDemoCompanionBookingMessage,
  DEMO_COMPANION_BOOKING_MESSAGES,
} from '../data/companionsData';
import { useLanguage } from '../i18n/LanguageContext';
import { getCurrentDateString, getCurrentTimeString } from '../utils/dateUtils';
import { getCompanionRateForService } from '../utils/companionServiceMatcher';
import { calculateBookingPricing, MIN_HOURLY_RATE } from '../utils/pricingStructure';
import { BookingMeetingLocationPicker } from './location/BookingMeetingLocationPicker';
import { StructuredLocation } from '../types/location';

interface CompanionDetailModalProps {
  companion: Companion | null;
  onClose: () => void;
  isWishlisted: boolean;
  onToggleWishlist: (id: string, e: React.MouseEvent) => void;
  onProceedToBooking: (booking: BookingDetails) => void;
  initialOccasion?: string;
}

export const CompanionDetailModal: React.FC<CompanionDetailModalProps> = ({
  companion,
  onClose,
  isWishlisted,
  onToggleWishlist,
  onProceedToBooking,
  initialOccasion,
}) => {
  const galleryPhotos = Array.from(
    new Set([companion?.image, ...(companion?.gallery || [])])
  ).filter(Boolean) as string[];

  const [selectedImage, setSelectedImage] = useState(companion?.image || '');
  const [selectedDate, setSelectedDate] = useState(getCurrentDateString());
  const [selectedTime, setSelectedTime] = useState(getCurrentTimeString());
  const [durationHours, setDurationHours] = useState(3);
  const [selectedOccasion, setSelectedOccasion] = useState(
    initialOccasion || companion?.supportedOccasions?.[0] || 'Dinner'
  );
  const [selectedCity, setSelectedCity] = useState(companion?.city || 'Kolkata');
  const [meetingLocation, setMeetingLocation] = useState(
    companion?.city ? `${companion.city} - Public Cafe / Banquet Hall` : 'Public Cafe / Banquet Hall'
  );
  const [meetingLocationDetails, setMeetingLocationDetails] = useState<StructuredLocation | null>(null);
  const [specialNotes, setSpecialNotes] = useState('');

  // Lightbox Modal state for full client gallery viewing
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (initialOccasion) {
      setSelectedOccasion(initialOccasion);
    }
  }, [initialOccasion]);

  useEffect(() => {
    if (companion) {
      setSelectedImage(companion.image);
      setSelectedCity(companion.city);
      setMeetingLocation(`${companion.city} - Public Cafe / Banquet Hall`);
    }
  }, [companion]);

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % galleryPhotos.length);
      }
      if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + galleryPhotos.length) % galleryPhotos.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, galleryPhotos.length]);

  // Booking Package & Tier: 'hourly' | 'one_third_day' (6h) | 'half_day' (12h) | 'full_day' (24h)
  const [bookingTier, setBookingTier] = useState<BookingTier>('hourly');

  // Authoritative automatic pricing calculation (85% Companion Payout / 15% Platform Fee)
  const pricing = useMemo(() => {
    if (!companion) {
      return {
        bookingTier: 'hourly' as BookingTier,
        durationHours: 2,
        packageTitle: 'Hourly Booking',
        baseAmount: 0,
        hourlyRate: MIN_HOURLY_RATE,
        rateOneThirdDay: 2000,
        rateHalfDay: 3000,
        rateFullDay: 5000,
        platformFee: 0,
        companionPayout: 0,
        taxes: 0,
        totalPayable: 0,
        calculationBreakdown: '',
      };
    }
    return calculateBookingPricing(companion, bookingTier, durationHours, selectedOccasion);
  }, [companion, bookingTier, durationHours, selectedOccasion]);

  const { language, t } = useLanguage();
  const isDemoCompanion = isDemoCompanionProfile(companion);
  const [demoBookingNotice, setDemoBookingNotice] = useState<string | null>(null);
  const [showSampleNotAvailableModal, setShowSampleNotAvailableModal] = useState<boolean>(false);

  // Driver verification enforcement rule
  const isDriverSelected = selectedOccasion === 'Driver';
  const isDriverVerified = Boolean(
    companion?.drivingLicenceStatus === 'APPROVED' || companion?.isDriverApproved
  );

  const handleBookingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companion) return;

    if (isDemoCompanion) {
      setDemoBookingNotice(
        'Not available on this time / date. Not Available for This Booking Time. This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.'
      );
      setShowSampleNotAvailableModal(true);
      return;
    }

    // Driver verification guard
    if (isDriverSelected && !isDriverVerified) {
      return;
    }

    const details: BookingDetails = {
      companion,
      date: selectedDate,
      time: selectedTime,
      durationHours: pricing.durationHours,
      bookingTier,
      packageTitle: pricing.packageTitle,
      occasion: selectedOccasion,
      serviceCategory: selectedOccasion,
      meetingLocation,
      meeting_location_details: meetingLocationDetails || undefined,
      city: selectedCity,
      specialRequests: specialNotes,
      totalAmount: pricing.totalPayable,
      baseAmount: pricing.baseAmount,
      taxes: pricing.taxes,
      platformFee: pricing.platformFee,
      companionPayout: pricing.companionPayout,
    };
    onProceedToBooking(details);
  };

  if (!companion) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 animate-in fade-in duration-200">
      <div
        id="companion-detail-modal"
        className="relative bg-white rounded-3xl max-w-4xl w-full overflow-hidden shadow-2xl border border-pink-100 flex flex-col max-h-[92vh]"
      >
        {/* Modal Top Header */}
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {isDemoCompanion ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold bg-amber-50 text-amber-800 border border-amber-300 px-3 py-1 rounded-full">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                <span>Not available on this time / date</span>
              </span>
            ) : (
              <>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className="text-xs font-extrabold text-slate-800">Verified Companion Profile</span>
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200/80 px-2 py-0.5 rounded-full">
                  <ShieldCheck className="w-3 h-3 text-emerald-600" /> 100% ID Safe
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => onToggleWishlist(companion.id, e)}
              className={`p-2 rounded-full border transition-all ${
                isWishlisted
                  ? 'bg-pink-500 text-white border-pink-500'
                  : 'bg-slate-50 text-slate-600 hover:text-pink-600 border-slate-200'
              }`}
              title="Save to favorites"
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-white' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-8">
          
          {/* Top section: Gallery & Key summary */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            {/* Gallery Column */}
            <div className="md:col-span-5 space-y-3">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-100 shadow-md group">
                <img
                  src={selectedImage}
                  alt={companion.name}
                  className="w-full h-full object-cover object-top cursor-pointer transition-transform duration-300 group-hover:scale-102"
                  referrerPolicy="no-referrer"
                  onClick={() => {
                    const idx = galleryPhotos.indexOf(selectedImage);
                    setLightboxIndex(idx >= 0 ? idx : 0);
                    setIsLightboxOpen(true);
                  }}
                />

                {/* Lightbox Trigger Button */}
                <button
                  type="button"
                  onClick={() => {
                    const idx = galleryPhotos.indexOf(selectedImage);
                    setLightboxIndex(idx >= 0 ? idx : 0);
                    setIsLightboxOpen(true);
                  }}
                  className="absolute bottom-3 right-3 px-2.5 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-900 text-white text-[11px] font-bold flex items-center gap-1 backdrop-blur-xs transition-all opacity-90 group-hover:opacity-100 cursor-pointer shadow-sm"
                  title="View Full Gallery"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span>View Gallery ({galleryPhotos.length})</span>
                </button>
              </div>

              {/* Thumbnails */}
              {galleryPhotos.length > 1 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 px-0.5">
                    <span>Photo Gallery ({galleryPhotos.length}/6)</span>
                    <span className="text-[10px] text-pink-600 font-semibold">Click to preview</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {galleryPhotos.map((imgUrl, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedImage(imgUrl)}
                        className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all shrink-0 cursor-pointer ${
                          selectedImage === imgUrl ? 'border-pink-500 scale-105 shadow-xs' : 'border-slate-200 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img src={imgUrl} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Profile Overview Column */}
            <div className="md:col-span-7 space-y-4">
              <div>
                <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 mb-2">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                    {companion.name}, {companion.age}
                  </h2>
                  {companion.isVerified && (
                    <div
                      id="verified-companion-badge"
                      className="group relative inline-flex items-center gap-2 px-3 sm:px-3.5 py-1 sm:py-1.5 rounded-full bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 text-white font-extrabold text-xs sm:text-sm shadow-md shadow-emerald-600/25 hover:shadow-lg hover:shadow-emerald-600/40 transition-all duration-300 hover:scale-105 cursor-default select-none border border-emerald-400/50"
                      title="100% Identity Verified & Background Checked Companion"
                    >
                      {/* Ambient breathing glow */}
                      <span className="absolute -inset-0.5 rounded-full bg-emerald-400/40 blur-xs animate-pulse pointer-events-none" />

                      {/* Animated Ping Core */}
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-80" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                      </span>

                      <ShieldCheck className="relative w-4 h-4 text-white drop-shadow-xs" />
                      <span className="relative tracking-wide font-black flex items-center gap-1.5">
                        Verified Companion
                        <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-spin" style={{ animationDuration: '6s' }} />
                      </span>
                    </div>
                  )}
                </div>

                {/* Micro trust verification line */}
                {companion.isVerified && (
                  <div className="mb-3 inline-flex items-center gap-2 px-2.5 py-1 bg-emerald-50/80 border border-emerald-200/70 rounded-xl text-[11px] text-emerald-800 font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Govt ID Verified • Background Vetted • Safe Meeting Guarantee</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-bold text-slate-800">{companion.rating}</span>
                    <span>({companion.reviewsCount} reviews)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-pink-500" />
                    <span className="font-semibold text-slate-800">
                      {companion.city}{companion.state ? `, ${companion.state}` : ''}
                    </span>
                  </div>
                </div>

                {/* Coverage cities pill */}
                {companion.coverageCities && companion.coverageCities.length > 0 && (
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px] text-slate-600 bg-slate-50 border border-slate-200/80 px-2.5 py-1 rounded-xl">
                    <span className="font-bold text-pink-600">Available in:</span>
                    <span>{companion.coverageCities.join(', ')}</span>
                  </div>
                )}
              </div>

              {/* Rate pill */}
              <div className="p-3 bg-pink-50/80 rounded-2xl border border-pink-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Hourly Booking Rate</p>
                  <p className="text-xl font-extrabold text-slate-900">₹{companion.hourlyRate} <span className="text-xs font-normal text-slate-500">/ hour</span></p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full">
                    ● Available for instant booking
                  </span>
                </div>
              </div>

              {/* Bio */}
              <p className="text-sm text-slate-600 leading-relaxed font-normal">
                {companion.about}
              </p>

              {/* Key Quick Facts Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-pink-500 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase font-semibold">Profession</p>
                    <p className="font-bold text-slate-800">{companion.profession}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-purple-500 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase font-semibold">Education</p>
                    <p className="font-bold text-slate-800">{companion.education}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-indigo-500 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase font-semibold">Languages</p>
                    <p className="font-bold text-slate-800">{companion.languages.join(', ')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-rose-500 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase font-semibold">Height</p>
                    <p className="font-bold text-slate-800">{companion.height}</p>
                  </div>
                </div>
              </div>

              {/* Occasions Supported */}
              <div>
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">
                  Ideal Occasions & Events
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {companion.supportedOccasions.map((occ, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-100 rounded-full"
                    >
                      {occ}
                    </span>
                  ))}
                </div>
              </div>

              {/* Interests */}
              <div>
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">
                  Interests & Hobbies
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {companion.interests.map((int, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 text-xs font-medium text-pink-700 bg-pink-50 border border-pink-100 rounded-full"
                    >
                      #{int}
                    </span>
                  ))}
                </div>
              </div>

            </div>

          </div>

          {/* Verification & Trust badges */}
          {isDemoCompanion ? (
            <div className="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Showcase Profile</h4>
                  <p className="text-xs text-slate-600">Preview showcase profile only — not available for live bookings</p>
                </div>
              </div>
              <span className="text-xs font-bold text-amber-800 bg-amber-100 px-3 py-1 rounded-full shrink-0">
                Showcase
              </span>
            </div>
          ) : (
            <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Verified Background & Identity</h4>
                  <p className="text-xs text-slate-600">Aadhaar Govt ID checked • Criminal record cleared • In-person interviewed</p>
                </div>
              </div>
              <span className="text-xs font-bold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full shrink-0">
                100% Safe Guaranteed
              </span>
            </div>
          )}

          {/* Interactive Booking Form */}
          <div className="bg-gradient-to-br from-pink-50/50 via-white to-purple-50/50 rounded-3xl p-6 border border-pink-200">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span>Book an Occasion with {companion.name}</span>
              <Sparkles className="w-4 h-4 text-pink-500" />
            </h3>

            {/* DRIVER VERIFICATION STATUS BANNER */}
            {isDriverSelected && (
              <div
                className={`p-3.5 rounded-2xl border text-xs flex items-start gap-2.5 mb-4 ${
                  isDriverVerified
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-amber-50 border-amber-300 text-amber-900'
                }`}
              >
                {isDriverVerified ? (
                  <>
                    <Car className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold block text-emerald-950">Verified Licensed Driver ✅</strong>
                      <span>
                        Government Driving Licence verified and approved by Admin. Authorized to accept driving & transit bookings.
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold block text-amber-950">Driver Licence Verification Pending ⏳</strong>
                      <span>
                        This companion cannot accept Driver bookings until their Driving Licence has been successfully verified and approved by the Admin. Please choose another service or book an approved driver.
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* TOURIST GUIDE BADGE */}
            {selectedOccasion === 'Tourist Guide' && (
              <div className="p-3 rounded-2xl bg-teal-50 border border-teal-200 text-xs text-teal-900 flex items-center gap-2 mb-4">
                <Compass className="w-4 h-4 text-teal-600 shrink-0" />
                <span>
                  <strong>Local Tourist Guide:</strong> Specialized in monument tours, city heritage walks, and local sightseeing.
                </span>
              </div>
            )}

            {/* 1. BOOKING TYPE / PACKAGE SELECTOR (Hourly, 1/3 Day, Half-Day, Full-Day) */}
            <div className="space-y-1.5 mb-4">
              <label className="block text-xs font-bold text-slate-800">
                Select Booking Duration & Package Structure
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setBookingTier('hourly');
                    setDurationHours(2);
                  }}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                    bookingTier === 'hourly'
                      ? 'bg-pink-50 border-pink-500 ring-2 ring-pink-500/20'
                      : 'bg-white border-slate-200 hover:border-pink-300'
                  }`}
                >
                  <span className="block text-[11px] font-bold text-slate-900">1. Hourly</span>
                  <span className="text-[10px] text-slate-500 block">Min ₹600/hr</span>
                  <span className="text-xs font-black text-pink-600 font-mono mt-0.5 block">
                    ₹{pricing.hourlyRate}/hr
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setBookingTier('one_third_day');
                    setDurationHours(6);
                  }}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                    bookingTier === 'one_third_day'
                      ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-500/20'
                      : 'bg-white border-slate-200 hover:border-purple-300'
                  }`}
                >
                  <span className="block text-[11px] font-bold text-slate-900">2. 1/3 Day</span>
                  <span className="text-[10px] text-slate-500 block">6 Hours Package</span>
                  <span className="text-xs font-black text-purple-600 font-mono mt-0.5 block">
                    ₹{pricing.rateOneThirdDay}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setBookingTier('half_day');
                    setDurationHours(12);
                  }}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                    bookingTier === 'half_day'
                      ? 'bg-indigo-50 border-indigo-500 ring-2 ring-indigo-500/20'
                      : 'bg-white border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  <span className="block text-[11px] font-bold text-slate-900">3. Half-Day</span>
                  <span className="text-[10px] text-slate-500 block">12 Hours Package</span>
                  <span className="text-xs font-black text-indigo-600 font-mono mt-0.5 block">
                    ₹{pricing.rateHalfDay}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setBookingTier('full_day');
                    setDurationHours(24);
                  }}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                    bookingTier === 'full_day'
                      ? 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/20'
                      : 'bg-white border-slate-200 hover:border-emerald-300'
                  }`}
                >
                  <span className="block text-[11px] font-bold text-slate-900">4. Full-Day</span>
                  <span className="text-[10px] text-slate-500 block">24 Hours Package</span>
                  <span className="text-xs font-black text-emerald-600 font-mono mt-0.5 block">
                    ₹{pricing.rateFullDay}
                  </span>
                </button>
              </div>
            </div>

            <form onSubmit={handleBookingSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Select Date
                  </label>
                  <input
                    type="date"
                    required
                    min={getCurrentDateString()}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Select Start Time
                  </label>
                  <input
                    type="time"
                    required
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Duration (Hours)
                  </label>
                  {bookingTier === 'hourly' && (
                    <select
                      value={durationHours}
                      onChange={(e) => setDurationHours(Number(e.target.value))}
                      className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                    >
                      <option value={1}>1 Hour</option>
                      <option value={2}>2 Hours</option>
                      <option value={3}>3 Hours</option>
                      <option value={4}>4 Hours</option>
                      <option value={5}>5 Hours</option>
                      <option value={6}>6 Hours</option>
                      <option value={8}>8 Hours</option>
                    </select>
                  )}
                  {bookingTier === 'one_third_day' && (
                    <select
                      value={durationHours}
                      onChange={(e) => setDurationHours(Number(e.target.value))}
                      className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 font-semibold"
                    >
                      <option value={4}>4 Hours (1/3 Day Rate applies)</option>
                      <option value={5}>5 Hours (1/3 Day Rate applies)</option>
                      <option value={6}>6 Hours (Full 1/3 Day)</option>
                    </select>
                  )}
                  {bookingTier === 'half_day' && (
                    <select
                      value={durationHours}
                      onChange={(e) => setDurationHours(Number(e.target.value))}
                      className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                    >
                      <option value={8}>8 Hours (Half-Day Rate applies)</option>
                      <option value={10}>10 Hours (Half-Day Rate applies)</option>
                      <option value={12}>12 Hours (Full Half-Day)</option>
                    </select>
                  )}
                  {bookingTier === 'full_day' && (
                    <select
                      value={durationHours}
                      onChange={(e) => setDurationHours(Number(e.target.value))}
                      className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-semibold"
                    >
                      <option value={18}>18 Hours (Full-Day Rate applies)</option>
                      <option value={24}>24 Hours (Full 24-Hour Day)</option>
                    </select>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Occasion / Service Category
                  </label>
                  <select
                    value={selectedOccasion}
                    onChange={(e) => setSelectedOccasion(e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    {OCCASIONS_LIST.map((occ, i) => (
                      <option key={i} value={occ}>{occ}</option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <BookingMeetingLocationPicker
                    initialLocationName={meetingLocation}
                    onLocationConfirmed={(loc) => {
                      setMeetingLocation(loc.formatted_address);
                      setSelectedCity(loc.city || loc.state || companion.city || 'Kolkata');
                      setMeetingLocationDetails(loc);
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Dress Code & Special Request Notes (Optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Traditional Indian ethnic attire preferred for wedding ceremony or navigation preferences..."
                  value={specialNotes}
                  onChange={(e) => setSpecialNotes(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              {/* Pricing summary matching 85% Companion Payout / 15% Platform Fee policy */}
              {isDemoCompanion && (
                <div
                  id="demo-companion-booking-notice"
                  role="alert"
                  className="p-4 bg-amber-50 border border-amber-300 rounded-2xl flex items-start gap-3 text-xs text-amber-950"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-1.5 flex-1">
                    <p className="font-extrabold text-amber-900 text-sm">
                      Not available on this time / date
                    </p>
                    <p className="text-xs font-bold text-amber-800">
                      Not Available for This Booking Time
                    </p>
                    <p className="font-semibold text-amber-900">
                      This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.
                    </p>
                    <p className="text-[11px] font-medium text-amber-800">
                      {demoBookingNotice || getDemoCompanionBookingMessage(language)}
                    </p>
                    {language !== 'bn' && (
                      <p className="text-[11px] text-amber-800">{DEMO_COMPANION_BOOKING_MESSAGES.bn}</p>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-900 text-white font-bold text-[11px] hover:bg-slate-900 transition-colors cursor-pointer"
                    >
                      <span>Return to Verified Companion List</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              <div className="p-4 bg-white rounded-2xl border border-pink-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1 text-xs text-slate-600">
                  <p>
                    <span className="font-semibold text-slate-900">{pricing.packageTitle}:</span>{' '}
                    <span className="font-bold text-slate-900">₹{pricing.baseAmount}</span>{' '}
                    <span className="text-[11px] text-slate-400">({pricing.calculationBreakdown})</span>
                  </p>
                  <p className="text-emerald-700">
                    Companion Earning (85% Net Share): <strong className="font-bold">₹{pricing.companionPayout}</strong>
                  </p>
                  <p className="text-slate-500">
                    Platform Fee (15%): <strong>₹{pricing.platformFee}</strong>
                  </p>
                  <p>GST (5%): <span className="font-bold text-slate-900">₹{pricing.taxes}</span></p>
                </div>

                <div className="flex items-center gap-4 justify-between sm:justify-end">
                  <div className="text-right">
                    <p className="text-[11px] text-slate-400 uppercase font-semibold">Total Payable</p>
                    <p className="text-2xl font-extrabold text-pink-600 font-mono">₹{pricing.totalPayable}</p>
                  </div>

                  <button
                    id="modal-proceed-booking-btn"
                    type="submit"
                    disabled={isDemoCompanion || (isDriverSelected && !isDriverVerified)}
                    className={`px-6 py-3 font-bold text-sm rounded-2xl shadow-lg transition-all ${
                      isDemoCompanion
                        ? 'bg-slate-300 text-slate-600 opacity-90 cursor-not-allowed shadow-none'
                        : isDriverSelected && !isDriverVerified
                        ? 'bg-slate-400 text-white opacity-80 cursor-not-allowed'
                        : 'bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white shadow-pink-500/30 hover:scale-105 active:scale-95 cursor-pointer'
                    }`}
                  >
                    {isDemoCompanion
                      ? 'Showcase Profile — Unavailable for Booking'
                      : isDriverSelected && !isDriverVerified
                      ? 'Driver Licence Pending'
                      : t('discovery.bookNow', 'Book Companion Now')}
                  </button>
                </div>
              </div>

            </form>
          </div>

        </div>

      </div>

      {/* Sample Companion Non-Bookable Modal */}
      {showSampleNotAvailableModal && (
        <div
          id="sample-companion-not-available-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-70 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setShowSampleNotAvailableModal(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 border border-amber-200 shadow-2xl space-y-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900">
              Not available on this time / date
            </h3>
            <p className="text-xs font-bold text-amber-800">
              Not Available for This Booking Time
            </p>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.
            </p>
            <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
              Not available on this time / date. Not Available for This Booking Time. Please choose an available verified companion.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowSampleNotAvailableModal(false);
                  onClose();
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
              >
                Choose an Available Verified Companion
              </button>
              <button
                type="button"
                onClick={() => setShowSampleNotAvailableModal(false)}
                className="py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors cursor-pointer"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Lightbox Gallery Modal for Client Viewing */}
      {isLightboxOpen && (
        <div
          id="companion-client-lightbox"
          className="fixed inset-0 z-60 bg-slate-950/90 backdrop-blur-md flex flex-col items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Header */}
          <div
            className="w-full max-w-4xl flex items-center justify-between text-white pb-3 select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-pink-400" />
              <span className="text-sm font-bold">{companion.name}&apos;s Photo Gallery</span>
              <span className="text-xs text-slate-300 ml-2 px-2.5 py-0.5 rounded-full bg-white/10">
                Photo {lightboxIndex + 1} of {galleryPhotos.length}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsLightboxOpen(false)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              title="Close gallery"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Photo View with Left/Right navigation */}
          <div
            className="relative w-full max-w-4xl flex-1 flex items-center justify-center max-h-[75vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {galleryPhotos.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setLightboxIndex((prev) => (prev - 1 + galleryPhotos.length) % galleryPhotos.length)
                }
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors cursor-pointer"
                title="Previous Photo (Left Arrow)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            <img
              src={galleryPhotos[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1}`}
              className="max-h-[75vh] max-w-full object-contain rounded-2xl shadow-2xl"
              referrerPolicy="no-referrer"
            />

            {galleryPhotos.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setLightboxIndex((prev) => (prev + 1) % galleryPhotos.length)
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors cursor-pointer"
                title="Next Photo (Right Arrow)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Thumbnail Strip */}
          {galleryPhotos.length > 1 && (
            <div
              className="w-full max-w-2xl flex items-center justify-center gap-2 pt-4 overflow-x-auto select-none"
              onClick={(e) => e.stopPropagation()}
            >
              {galleryPhotos.map((url, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIndex(idx)}
                  className={`w-12 h-12 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                    lightboxIndex === idx
                      ? 'border-pink-500 scale-110 shadow-md'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
